/**
 * React Query hooks for Storefront Page Config CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { frappe } from "@/lib/frappe-client";
import { useNavigate } from "react-router-dom";
import { routes } from "@/lib/config/routes";
import { getQueryOptions } from "@/lib/config/queryConfig";
import { logger } from "@/lib/utils/logger";
import type { StorefrontPageConfigDoc } from "../types";
import { storefrontPageConfigQueryKeys } from "./queryKeys";

/**
 * Hook for fetching a single Storefront Page Config detail
 */
export function useStorefrontPageConfigDetail(name: string) {
  return useQuery<StorefrontPageConfigDoc>({
    queryKey: storefrontPageConfigQueryKeys.detail(name),
    queryFn: () => frappe.get<StorefrontPageConfigDoc>({
      doctype: "Storefront Page Config",
      name,
    }),
    enabled: !!name && name !== "new",
    ...getQueryOptions("detail"),
  });
}

/**
 * Hook for creating a new Storefront Page Config
 */
export function useCreateStorefrontPageConfig() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<StorefrontPageConfigDoc, Error, Partial<StorefrontPageConfigDoc>>({
    mutationFn: (data: Partial<StorefrontPageConfigDoc>) => {
      return frappe.create<StorefrontPageConfigDoc>({
        doctype: "Storefront Page Config",
        data,
      });
    },
    onSuccess: (data) => {
      const pageConfigName = data.name;
      
      // Update cache for the new page config
      if (pageConfigName) {
        queryClient.setQueryData(storefrontPageConfigQueryKeys.detail(pageConfigName), data);
      }
      
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: storefrontPageConfigQueryKeys.list() });
      
      // Redirect to detail page
      if (pageConfigName) {
        navigate(routes.storefrontPageConfig.detail(pageConfigName));
      }
    },
  });
}

/**
 * Hook for saving an existing Storefront Page Config
 */
export function useSaveStorefrontPageConfig(name: string) {
  const queryClient = useQueryClient();

  return useMutation<StorefrontPageConfigDoc, Error, Partial<StorefrontPageConfigDoc>>({
    mutationFn: (data: Partial<StorefrontPageConfigDoc>) => {
      return frappe.update<StorefrontPageConfigDoc>({
        doctype: "Storefront Page Config",
        name,
        data,
      });
    },
    onSuccess: (data) => {
      // Update cache
      queryClient.setQueryData(storefrontPageConfigQueryKeys.detail(name), data);
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: storefrontPageConfigQueryKeys.list() });
    },
    onError: (error) => {
      logger.error('[useSaveStorefrontPageConfig] onError:', error);
    },
  });
}

/**
 * Hook for deleting a Storefront Page Config
 */
export function useDeleteStorefrontPageConfig(name: string) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<void, Error, void>({
    mutationFn: () => {
      return frappe.delete({
        doctype: "Storefront Page Config",
        name,
      });
    },
    onSuccess: () => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: storefrontPageConfigQueryKeys.detail(name) });
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: storefrontPageConfigQueryKeys.list() });
      // Navigate to list page
      navigate(routes.storefrontPageConfig.list);
    },
  });
}

/**
 * Hook for publishing a Storefront Page Config
 */
export function usePublishStorefrontPageConfig() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; version?: string; error?: string },
    Error,
    { docname: string }
  >({
    mutationFn: async ({ docname }) => {
      return frappe.call<{ success: boolean; version?: string; error?: string }>(
        "zaviago_backend.storefront.publish.publish_page_config",
        { docname }
      );
    },
    onSuccess: (_data, variables) => {
      // Refresh publish status fields on the doc and any lists
      queryClient.invalidateQueries({ queryKey: storefrontPageConfigQueryKeys.detail(variables.docname) });
      queryClient.invalidateQueries({ queryKey: storefrontPageConfigQueryKeys.list() });
    },
  });
}
